import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {EyeIcon} from './EyeIcon';
import {EyeOffIcon} from './EyeOffIcon';

/** A column as the menu needs to present it. */
export interface VisibilityMenuColumn<K extends string> {
	key: K;
	label: string;
}

interface Props<K extends string> {
	/** The toggleable columns only — mandatory columns have nothing to offer here. */
	columns: readonly VisibilityMenuColumn<K>[];
	isVisible: (key: K) => boolean;
	onToggle: (key: K) => void;
	/**
	 * Viewport width below which the control is hidden, in px. Each table decides
	 * its own: hiding columns is meaningless on a layout that has already dropped
	 * to a narrow presentation.
	 */
	hideBelowPx: number;
	'data-testid'?: string;
}

const Container = styled.div<{$hideBelowPx: number}>`
	position: relative;
	display: block;
	@media (max-width: ${p => p.$hideBelowPx - 1}px) {
		display: none;
	}
`;
// The open-state tint is layered onto the design-system Button itself, so these
// declarations follow the Button's own in the stylesheet and win. A surrounding
// wrapper could not colour the trigger, because the Button always sets its own
// `color`.
const TriggerButton = styled(LabelledButton)<{$open: boolean}>`
	background: ${p => (p.$open ? p.theme.sys.color.background.primary['1'] : 'transparent')};
	color: ${p => (p.$open ? p.theme.sys.color.brand.primary['1'] : p.theme.sys.color.text.content.main)};
`;
// Fixed and body-portalled, so no ancestor overflow can clip it — the same
// escape the toolbar's Group by dropdown already uses. `max-height` with its
// own scroll keeps a long column list from running the full height of the
// screen when there are more toggles than fit.
//
// The stacking order sits just under the modal and drawer layer, which this tree
// puts at 50: a column menu is a popover over the page, never something that
// should paint over an open modal. It is also the layer the design-system
// dropdown behind the sibling Group by control renders at, so the two toolbar
// menus stack consistently.
const Menu = styled.div`
	position: fixed;
	z-index: 49;
	width: 208px;
	max-height: min(70vh, 480px);
	overflow-y: auto;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	padding: ${p => p.theme.sys.spacing[200]} 0;
	${raisedSurfaceShadow}
`;
const MenuHeading = styled.p`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin: 0;
	padding: 0 12px 8px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const MenuItemRow = styled.div<{$muted?: boolean}>`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-between;
	padding: 6px 12px;
	color: ${p => (p.$muted ? p.theme.sys.color.text.content.auxiliary : p.theme.sys.color.text.content.main)};
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const ItemLabel = styled.span`
	${p => p.theme.sys.font?.body?.['2']}
`;
const VisibleIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.sys.color.text.content.main};
`;
const HiddenIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Divider = styled.hr`
	margin: 6px 0;
	border: 0;
	border-top: 1px solid ${p => p.theme.sys.color.border.decorative};
`;

const MENU_WIDTH = 208;
const MENU_GAP = 4;

interface MenuPosition {
	top: number;
	left: number;
}

/**
 * Places the menu below the trigger and right-aligned to it, in viewport
 * coordinates so `position: fixed` can consume them directly. Clamped to the
 * viewport so a trigger near the right edge does not push the menu offscreen.
 */
function computeMenuPosition(triggerRect: DOMRect): MenuPosition {
	const top = triggerRect.bottom + MENU_GAP;
	const preferredLeft = triggerRect.right - MENU_WIDTH;
	const maxLeft = window.innerWidth - MENU_WIDTH - 4;
	const left = Math.max(4, Math.min(preferredLeft, maxLeft));
	return {top, left};
}

/**
 * The eye-icon "show/hide columns" menu, shared by every ticketing table.
 *
 * Visible columns are listed first, then hidden ones below a divider, so the menu
 * doubles as a readout of the current table without the user having to compare it
 * against the header row.
 *
 * The open menu is portalled to `document.body` and positioned with
 * `position: fixed` from the trigger's bounding rect. An in-flow `position:
 * absolute` menu is clipped by any scrolling ancestor — the dashboard toolbar
 * scrolls horizontally, which makes it a scroll container on both axes and cuts
 * the popover off at its 48px height. Portalling escapes that entirely.
 */
export function ColumnsVisibilityMenu<K extends string>({columns, isVisible, onToggle, hideBelowPx, ...rest}: Props<K>) {
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// The container wraps only the trigger button (the menu is portalled to
	// body), so its bounding rect is the trigger's — no extra ref needed on the
	// design-system Button, which does not forward one.
	const updatePosition = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;
		// The trigger hides itself below its own breakpoint, and the menu lives in a
		// portal the same media query cannot reach. Narrowing the window while the
		// menu is open would otherwise strand it in the viewport corner with no
		// trigger to close it against. Read that breakpoint directly — the trigger's
		// box is all-zero both when it is hidden and when nothing has laid it out,
		// so it cannot tell the two apart.
		if (window.matchMedia?.(`(max-width: ${hideBelowPx - 1}px)`).matches) {
			setOpen(false);
			return;
		}
		setMenuPos(computeMenuPosition(container.getBoundingClientRect()));
	}, [hideBelowPx]);

	useLayoutEffect(() => {
		if (!open) return;
		updatePosition();
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;
		function onClickOutside(e: MouseEvent) {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (menuRef.current?.contains(target)) return;
			setOpen(false);
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') setOpen(false);
		}
		// The menu is anchored to the trigger in viewport coordinates, so it has to
		// follow the trigger when the page scrolls or the window resizes. Otherwise
		// scrolling the surface under it leaves the menu floating over stale space.
		function onReposition() {
			updatePosition();
		}
		document.addEventListener('mousedown', onClickOutside);
		document.addEventListener('keydown', onKey);
		window.addEventListener('resize', onReposition);
		window.addEventListener('scroll', onReposition, true);
		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('keydown', onKey);
			window.removeEventListener('resize', onReposition);
			window.removeEventListener('scroll', onReposition, true);
		};
	}, [open, updatePosition]);

	const visibleCols = columns.filter(col => isVisible(col.key));
	const hiddenCols = columns.filter(col => !isVisible(col.key));

	const menu = open && menuPos && (
		<Menu ref={menuRef} role="menu" style={{top: menuPos.top, left: menuPos.left}}>
			<MenuHeading>Columns</MenuHeading>
			{visibleCols.map(col => (
				<LabelledButton
					key={col.key}
					emphasis={EMPHASIS.MEDIUM}
					role="menuitem"
					label={col.label}
					onPress={() => onToggle(col.key)}
				>
					<MenuItemRow>
						<ItemLabel>{col.label}</ItemLabel>
						<VisibleIconWrap>
							<EyeIcon size={16} data-testid="eye-icon" />
						</VisibleIconWrap>
					</MenuItemRow>
				</LabelledButton>
			))}
			{visibleCols.length > 0 && hiddenCols.length > 0 && <Divider role="separator" />}
			{hiddenCols.map(col => (
				<LabelledButton
					key={col.key}
					emphasis={EMPHASIS.MEDIUM}
					role="menuitem"
					label={col.label}
					onPress={() => onToggle(col.key)}
				>
					<MenuItemRow $muted>
						<ItemLabel>{col.label}</ItemLabel>
						<HiddenIconWrap>
							<EyeOffIcon size={16} data-testid="eye-off-icon" />
						</HiddenIconWrap>
					</MenuItemRow>
				</LabelledButton>
			))}
		</Menu>
	);

	return (
		<Container ref={containerRef} $hideBelowPx={hideBelowPx}>
			<TriggerButton
				$open={open}
				emphasis={EMPHASIS.LOW}
				aria-haspopup="menu"
				aria-expanded={open}
				label="Show/hide columns"
				title="Show/hide columns"
				onPress={() => setOpen(o => !o)}
				data-testid={rest['data-testid']}
			>
				<EyeIcon size={16} />
			</TriggerButton>
			{menu && createPortal(menu, document.body)}
		</Container>
	);
}
